import type { PlanId } from "@/lib/branchTypes"

// When the frame ladder engages (the "flop down"): not at the plan click
// — the close-up holds while the line climbs into the red — but at the
// mid-2029 story marker in plan-a--2029-choose-a-path.md.
export const MOBILE_STRIP_FRAME_REVEAL_YEAR_FRACTION = 2029.5

// The 2029 choice beat: where plan A's center-glide camera move starts.
// (The band's own anchor is separately adjustable — see
// bandAnchorYearFraction in mobileStripBandSettings — so re-anchoring
// the red field can't move the camera.)
export const MOBILE_STRIP_CHOICE_BEAT_YEAR_FRACTION = 2029

// The flop's end: the start of the story year after the reveal beat.
export const FLOP_END_YEAR_FRACTION =
  Math.floor(MOBILE_STRIP_FRAME_REVEAL_YEAR_FRACTION) + 1

export type PlanFrameMode =
  | "freeze" // view never changes; the line (and red) fooms off the top
  | "flop-freeze" // the flop plays, then the frame freezes there
  | "flop-step" // flop, then the timeline steps a story year at a time
  | "flop-center" // flop, then the window keeps the date centered
  | "center-glide" // no flop: one eased move into the centered ride

// What the strip's FRAME does after the reader picks each plan. The
// takeover-risk band is independent of these modes — its anchor goes
// live at 2029.5 for every plan, so on foom endings the red rides the
// climbing line up and off the top. "flop-step" (the old production
// behavior) and "flop-center" are currently unused knob values.
export const MOBILE_STRIP_PLAN_FRAMES: Record<
  PlanId,
  { mode: PlanFrameMode }
> = {
  S: { mode: "freeze" },
  A: { mode: "center-glide" },
  B: { mode: "flop-freeze" },
  C: { mode: "flop-freeze" },
  D: { mode: "freeze" },
}

// Width of the centered post-flop window (current date at the middle).
export const MOBILE_STRIP_CENTERED_SPAN_YEARS = 3

export const planFrameModeFor = (planId: PlanId): PlanFrameMode =>
  MOBILE_STRIP_PLAN_FRAMES[planId]?.mode ?? "flop-step"

// The frame's clock: "freeze" parks just short of the flop beat,
// "flop-freeze" just short of the flop's end; other modes run live.
export const clampedFrameYearFraction = (
  mode: PlanFrameMode,
  storyYearFraction: number,
): number => {
  if (mode === "freeze") {
    return Math.min(
      storyYearFraction,
      MOBILE_STRIP_FRAME_REVEAL_YEAR_FRACTION - 0.001,
    )
  }
  if (mode === "flop-freeze") {
    return Math.min(storyYearFraction, FLOP_END_YEAR_FRACTION - 0.001)
  }
  return storyYearFraction
}
